import { useTranslation } from '@client-app/i18n';
import { Check } from '@phosphor-icons/react';
import clsx from 'clsx';
import React from 'react';
import {
  getPricingTierIntegrationConnections,
  getPricingTierBrandGroupLimit,
  getPricingTierLimits,
  getPricingTierPrice,
  isCustomPricingTierLimit,
  isUnlimitedPricingTierLimit,
  type PricingTierConnectionLimit,
  type PricingTierOption,
} from './pricingTierOptions';
import type { BillingCycle } from './teamOfferSelection';

interface PlanComparisonProps {
  tiers: readonly PricingTierOption[];
  billingCycle: BillingCycle;
}

type ComparisonRow = {
  id: string;
  label: string;
  value: (column: ComparisonColumn) => React.ReactNode;
};

type ComparisonColumn = {
  id: string;
  tier: PricingTierOption;
  isScale: boolean;
};

const formatNumber = (value: number) => new Intl.NumberFormat().format(value);

const formatLimit = (
  limit: number | 'unlimited' | 'custom',
  unlimitedLabel: string,
  customLabel: string,
) => {
  if (isCustomPricingTierLimit(limit)) return customLabel;
  if (isUnlimitedPricingTierLimit(limit)) return unlimitedLabel;
  return formatNumber(limit);
};

const formatConnectionLimit = (
  limit: PricingTierConnectionLimit,
  customLabel: string,
  unavailableLabel: string,
) => {
  if (limit === 'custom') return customLabel;
  if (limit === 0) return unavailableLabel;
  return formatNumber(limit);
};

const SupportIndicator = ({ supported, label }: { supported: boolean; label: string }) => (
  <span aria-label={label} className="inline-flex items-center justify-center">
    {supported ? (
      <Check aria-hidden="true" size={16} weight="bold" className="text-[#15803D]" />
    ) : (
      <span aria-hidden="true" className="text-[13px] font-medium leading-none text-[#8A93B2]">-</span>
    )}
  </span>
);

export const PlanComparison: React.FC<PlanComparisonProps> = ({ tiers, billingCycle }) => {
  const { t } = useTranslation();
  const scaleSourceTier = tiers.find((tier) => tier.id === 'team-enterprise');
  const columns: ComparisonColumn[] = [
    ...tiers.map((tier) => ({ id: tier.id, tier, isScale: false })),
    ...(scaleSourceTier ? [{ id: 'scale', tier: scaleSourceTier, isScale: true }] : []),
  ];
  const getColumnName = (column: ComparisonColumn) => (
    column.isScale
      ? t('website.pricing.scaleName')
      : column.tier.group === 'solo'
        ? t('website.pricing.soloName')
        : t('website.pricing.teamName')
  );
  const rows: Array<{ group?: string; items: ComparisonRow[] }> = [
    {
      group: t('website.pricing.comparisonWorkspace'),
      items: [
        {
          id: 'workspace-users',
          label: t('website.pricing.comparisonWorkspaceUsers'),
          value: ({ tier, isScale }) => (
            isScale
              ? t('website.pricing.custom')
              : formatLimit(
                getPricingTierLimits(tier, billingCycle).workspaceUsers,
                t('website.pricing.unlimited'),
                t('website.pricing.custom'),
              )
          ),
        },
        {
          id: 'brand-groups',
          label: t('website.pricing.comparisonBrandGroups'),
          value: ({ tier, isScale }) => {
            if (isScale) return t('website.pricing.custom');
            const limit = getPricingTierBrandGroupLimit(tier);
            if (limit === 0) return t('website.pricing.notIncluded');
            if (isUnlimitedPricingTierLimit(limit)) return t('website.pricing.unlimited');
            return t('website.pricing.comparisonUpTo', { value: formatNumber(limit) });
          },
        },
      ],
    },
    {
      group: t('website.pricing.comparisonConnections'),
      items: [
        {
          id: 'stores',
          label: t('website.pricing.comparisonStores'),
          value: ({ tier, isScale }) => (
            isScale
              ? t('website.pricing.custom')
              : formatLimit(
                getPricingTierLimits(tier, billingCycle).storeConnections,
                t('website.pricing.unlimited'),
                t('website.pricing.custom'),
              )
          ),
        },
        {
          id: 'slack-feishu-connections',
          label: t('website.pricing.comparisonSlackAndFeishu'),
          value: ({ tier, isScale }) => (
            isScale ? t('website.pricing.custom') : (
              <SupportIndicator
                supported={getPricingTierIntegrationConnections(tier).supportsSlackAndFeishu}
                label={getPricingTierIntegrationConnections(tier).supportsSlackAndFeishu
                  ? t('website.pricing.comparisonSupported')
                  : t('website.pricing.comparisonNotSupported')}
              />
            )
          ),
        },
        {
          id: 'other-integrations',
          label: t('website.pricing.comparisonOtherIntegrations'),
          value: ({ tier, isScale }) => (
            isScale
              ? t('website.pricing.custom')
              : formatConnectionLimit(
                getPricingTierIntegrationConnections(tier).otherIntegrations,
                t('website.pricing.custom'),
                t('website.pricing.notIncluded'),
              )
          ),
        },
        {
          id: 'total-connections',
          label: t('website.pricing.comparisonTotalConnections'),
          value: ({ tier, isScale }) => (
            isScale
              ? t('website.pricing.custom')
              : formatConnectionLimit(
                getPricingTierIntegrationConnections(tier).total,
                t('website.pricing.custom'),
                t('website.pricing.notIncluded'),
              )
          ),
        },
      ],
    },
    {
      group: t('website.pricing.comparisonWorkUnits'),
      items: [
        {
          id: 'work-units',
          label: t('website.pricing.comparisonMonthlyWorkUnits'),
          value: ({ tier, isScale }) => (
            isScale ? t('website.pricing.custom') : formatNumber(tier.workUnits || 0)
          ),
        },
        {
          id: 'estimated-tasks',
          label: t('website.pricing.comparisonEstimatedTasks'),
          value: ({ tier, isScale }) => (
            isScale
              ? t('website.pricing.custom')
              : `${formatNumber(tier.estimatedMonthlyOperations.min)}–${formatNumber(tier.estimatedMonthlyOperations.max)}`
          ),
        },
      ],
    },
  ];

  return (
    <section className="mt-10 w-full min-w-0 max-w-[1180px] self-center" aria-labelledby="plan-comparison-title">
      <div className="mb-4 px-0.5 text-center">
        <h3 id="plan-comparison-title" className="text-lg font-semibold tracking-tight text-text-main">
          {t('website.pricing.comparePlansTitle')}
        </h3>
      </div>

      <div
        aria-label={t('website.pricing.comparePlansTitle')}
        role="region"
        tabIndex={0}
        className="w-full max-w-full touch-pan-x overflow-x-auto overscroll-x-contain rounded-[20px] border border-[#E8E5E0] bg-white shadow-[0_8px_24px_rgba(33,31,27,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table className="w-max min-w-[1460px] border-collapse text-left">
          <thead>
            <tr className="border-b border-[#ECEAE6]">
              <th scope="col" className="sticky left-0 z-10 min-w-[210px] bg-white px-5 py-5">
                <span className="sr-only">{t('website.pricing.comparisonFeature')}</span>
              </th>
              {columns.map((column) => (
                <th key={column.id} scope="col" className="min-w-[112px] px-3 py-5 text-center align-top text-sm font-semibold text-text-main">
                  <div>{getColumnName(column)}</div>
                  <div className="mt-1 text-xs font-medium text-[#706C66]">
                    {column.isScale
                      ? t('website.pricing.custom')
                      : `$${formatNumber(getPricingTierPrice(column.tier, billingCycle))}${t('website.pricing.perMonth')}`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((section) => (
              <React.Fragment key={section.group}>
                <tr className="border-y border-[#ECEAE6] bg-[#F8F7F5]">
                  <th scope="rowgroup" className="sticky left-0 z-10 bg-[#F8F7F5] px-5 py-3 text-sm font-bold text-text-main">
                    {section.group}
                  </th>
                  {columns.map((column) => (
                    <td key={column.id} className="bg-[#F8F7F5]" />
                  ))}
                </tr>
                {section.items.map((row) => (
                  <tr key={row.id} className="border-b border-[#F0EEEA] last:border-b-0">
                    <th scope="row" className="sticky left-0 z-10 bg-white px-5 py-3.5 text-sm font-medium text-[#4F4C47]">
                      {row.label}
                    </th>
                    {columns.map((column) => (
                      <td
                        key={column.id}
                        className={clsx(
                          'px-3 py-3.5 text-center text-sm font-medium text-[#302E2A]',
                          column.tier.id === 'team-4999' && !column.isScale && 'bg-[#F6F8FF] text-[#1E5FE8]',
                        )}
                      >
                        {row.value(column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PlanComparison;
